import React, { useEffect, useState } from 'react';
import { Play, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { trackEvent } from '../lib/customerEventTracker';

// --- Swap these two lines once the real video is ready --------------------
// Paste the YouTube video ID (the part after "v=" or after "youtu.be/").
const YOUTUBE_VIDEO_ID = '';
const VIDEO_TITLE = 'See how SolveTax works';
// ---------------------------------------------------------------------------

const DISMISS_KEY = 'solvetax_video_widget_dismissed';
const BADGE_SRC = `${process.env.PUBLIC_URL}/favicon-32.png`;

const FloatingVideoWidget = () => {
  const { t } = useLanguage();
  const [dismissed, setDismissed] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') setDismissed(true);
    } catch {
      // sessionStorage unavailable — leave widget visible
    }
  }, []);

  if (dismissed) return null;

  const hasVideo = Boolean(YOUTUBE_VIDEO_ID);
  const thumbnailUrl = hasVideo
    ? `https://img.youtube.com/vi/${YOUTUBE_VIDEO_ID}/hqdefault.jpg`
    : null;

  const handleDismiss = (e) => {
    e.stopPropagation();
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // sessionStorage unavailable — widget just won't persist dismissal
    }
  };

  const handleOpen = () => {
    if (!hasVideo) return;
    setPlaying(true);
    trackEvent({
      event_name: 'cta_click',
      event_action: 'click',
      event_status: 'success',
      event_category: 'engagement',
      cta_name: 'video_widget_play',
    });
  };

  return (
    <>
      <div className="fixed left-5 lg:left-6 bottom-24 lg:bottom-6 z-40 w-64 sm:w-72">
        <button
          type="button"
          onClick={handleOpen}
          aria-label={t('videoWidget.play_aria', 'Play video: {title}').replace('{title}', VIDEO_TITLE)}
          className="group relative block w-full aspect-video overflow-hidden rounded-xl shadow-2xl shadow-black/30 ring-1 ring-black/10 bg-[#0f2540]"
        >
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#14A098] via-[#0f2540] to-[#0f2540]" />
          )}

          <div className="absolute inset-0 bg-black/10 group-hover:bg-black/25 transition-colors" />

          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex items-center justify-center w-12 h-12 rounded-full bg-white/95 shadow-lg transition-transform group-hover:scale-110">
              <Play className="w-5 h-5 text-[#e11d2e] fill-[#e11d2e]" aria-hidden />
            </span>
          </span>

          <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent px-3 pt-6 pb-2.5 flex items-center gap-2">
            <img
              src={BADGE_SRC}
              alt=""
              className="w-5 h-5 rounded-md shrink-0 shadow-sm"
              draggable={false}
            />
            <span className="text-left text-white text-[12px] font-semibold leading-tight line-clamp-2">
              {VIDEO_TITLE}
            </span>
          </span>
        </button>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('videoWidget.close_aria', 'Dismiss video preview')}
          className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full bg-[#0f2540] hover:bg-[#14A098] text-white flex items-center justify-center shadow-md ring-2 ring-white transition-colors"
        >
          <X className="w-3.5 h-3.5" aria-hidden />
        </button>
      </div>

      {playing && hasVideo && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPlaying(false)}
        >
          <div
            className="relative w-full max-w-3xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPlaying(false)}
              aria-label={t('videoWidget.close_aria', 'Close video')}
              className="absolute -top-10 right-0 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" aria-hidden />
            </button>
            <iframe
              className="w-full h-full rounded-lg shadow-2xl"
              src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0`}
              title={VIDEO_TITLE}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingVideoWidget;
